import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { api } from '../../context/AuthContext';
import { INTERNAL_KYC_UI } from '../../lib/featureFlags';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft, Plus, MagnifyingGlass, CheckCircle, Clock, Warning, XCircle,
  User, Coins, CurrencyEur, Calendar, FileText, Eye, Check, X, CreditCard,
  ListBullets, Buildings, ArrowRight, FunnelSimple, PencilSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AdminPageHeader, AdminKpi, AdminKpiStrip } from '../../components/admin/AdminPageHeader';
import AdminToolbar from '../../components/admin/AdminToolbar';
import { SubscriptionEditDialog } from '../../components/admin/SubscriptionEditDialog';

// Status configuration
const statusConfig = {
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  paid: { label: 'Payment Received', color: 'bg-green-100 text-green-700', icon: CreditCard },
  allocated: { label: 'Units Allocated', color: 'bg-emerald-100 text-emerald-700', icon: Coins },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: X },
};

/**
 * Subscription Detail Dialog
 */
const SubscriptionDetailDialog = ({ subscription, open, onOpenChange, onRefresh, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [unitsApproved, setUnitsApproved] = useState('');

  if (!subscription) return null;

  const status = statusConfig[subscription.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  const handleApprove = async () => {
    if (reason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/admin/v2/subscriptions/${subscription.id}/approve`, {
        reason,
        units_approved: unitsApproved ? parseInt(unitsApproved) : null
      });
      toast.success('Subscription approved');
      setAction(null);
      setReason('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (reason.length < 5 || !paymentRef) {
      toast.error('Payment reference and reason required');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/admin/v2/subscriptions/${subscription.id}/payment`, {
        reason,
        payment_reference: paymentRef
      });
      toast.success('Payment marked as received');
      setAction(null);
      setReason('');
      setPaymentRef('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark payment');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocate = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/v2/subscriptions/${subscription.id}/allocate`);
      toast.success('Units allocated successfully');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to allocate units');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (reason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/admin/v2/subscriptions/${subscription.id}/reject`, { reason });
      toast.success('Subscription rejected');
      setAction(null);
      setReason('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (reason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/admin/v2/subscriptions/${subscription.id}/cancel`, { reason });
      toast.success('Subscription cancelled');
      setAction(null);
      setReason('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', status.color.split(' ')[0])}>
              <StatusIcon className="h-5 w-5" weight="duotone" />
            </div>
            <div>
              <DialogTitle>Subscription {subscription.code}</DialogTitle>
              <DialogDescription className="sr-only">Subscription details and workflow actions</DialogDescription>
              <Badge className={cn('text-xs mt-1', status.color)}>{status.label}</Badge>
            </div>
          </div>
        </DialogHeader>

        {/* Subscription Details */}
        <div className="space-y-4">
          {/* Investor Info */}
          <Card className="p-4">
            <h4 className="text-sm font-medium text-foreground-muted mb-3 flex items-center gap-2">
              <User className="h-4 w-4" /> Investor
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-foreground-muted">Name</Label>
                <p className="font-medium">{subscription.party?.display_name || subscription.party_name || '—'}</p>
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Email</Label>
                <p className="text-sm">{subscription.party?.email || subscription.party_email || '—'}</p>
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Party Code</Label>
                <p className="font-mono text-sm">{subscription.party?.code || '—'}</p>
              </div>
              {INTERNAL_KYC_UI && (
                <div>
                  <Label className="text-xs text-foreground-muted">KYC Level</Label>
                  <Badge className="bg-gray-100 text-gray-700">{subscription.party?.kyc_level || '—'}</Badge>
                </div>
              )}
            </div>
          </Card>

          {/* Investment Info */}
          <Card className="p-4">
            <h4 className="text-sm font-medium text-foreground-muted mb-3 flex items-center gap-2">
              <Coins className="h-4 w-4" /> Investment Details
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-foreground-muted">Offering</Label>
                <p className="font-medium">{subscription.offering?.name || subscription.offering_name || '—'}</p>
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Project</Label>
                <p className="text-sm">{subscription.project_title || '—'}</p>
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Units Requested</Label>
                <p className="text-lg font-bold">{subscription.units_requested?.toLocaleString()}</p>
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Unit Price</Label>
                <p className="font-medium">€{parseFloat(subscription.unit_price || 0).toLocaleString()}</p>
              </div>
              <div>
                <Label className="text-xs text-foreground-muted">Total Amount</Label>
                <p className="text-lg font-bold text-primary">€{parseFloat(subscription.total_amount || 0).toLocaleString()}</p>
              </div>
              {subscription.units_approved && (
                <div>
                  <Label className="text-xs text-foreground-muted">Units Approved</Label>
                  <p className="font-bold text-green-600">{subscription.units_approved?.toLocaleString()}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Payment Info (if applicable) */}
          {(subscription.status === 'paid' || subscription.status === 'allocated') && (
            <Card className="p-4">
              <h4 className="text-sm font-medium text-foreground-muted mb-3 flex items-center gap-2">
                <CreditCard className="h-4 w-4" /> Payment Info
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-foreground-muted">Payment Reference</Label>
                  <p className="font-mono">{subscription.payment_reference || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Payment Date</Label>
                  <p>{subscription.payment_date ? new Date(subscription.payment_date).toLocaleDateString() : '—'}</p>
                </div>
              </div>
            </Card>
          )}

          {/* Timeline */}
          <Card className="p-4">
            <h4 className="text-sm font-medium text-foreground-muted mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Timeline
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-foreground-muted">Subscription date</span>
                <span>
                  {(() => {
                    // What the returns are calculated from, which is not always when it was typed in
                    const when = subscription.subscription_date || subscription.payment_date
                      || subscription.allocated_at || subscription.created_at;
                    return when ? new Date(when).toLocaleDateString() : '—';
                  })()}
                </span>
              </div>
              {subscription.approved_at && (
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Approved</span>
                  <span>{new Date(subscription.approved_at).toLocaleString()}</span>
                </div>
              )}
              {subscription.payment_marked_at && (
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Payment Received</span>
                  <span>{new Date(subscription.payment_marked_at).toLocaleString()}</span>
                </div>
              )}
              {subscription.allocated_at && (
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Units Allocated</span>
                  <span>{new Date(subscription.allocated_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Action Forms */}
          {action === 'approve' && (
            <Card className="p-4 border-blue-200 bg-blue-50">
              <h4 className="font-medium mb-3">Approve Subscription</h4>
              <div className="space-y-3">
                <div>
                  <Label>Units to Approve (leave blank for all {subscription.units_requested})</Label>
                  <Input
                    type="number"
                    value={unitsApproved}
                    onChange={(e) => setUnitsApproved(e.target.value)}
                    placeholder={subscription.units_requested?.toString()}
                  />
                </div>
                <div>
                  <Label>Approval Reason *</Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for approval (min 5 chars)"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleApprove} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                    {loading ? 'Processing...' : 'Confirm Approval'}
                  </Button>
                  <Button variant="outline" onClick={() => { setAction(null); setReason(''); }}>Cancel</Button>
                </div>
              </div>
            </Card>
          )}

          {action === 'payment' && (
            <Card className="p-4 border-green-200 bg-green-50">
              <h4 className="font-medium mb-3">Mark Payment Received</h4>
              <div className="space-y-3">
                <div>
                  <Label>Payment Reference *</Label>
                  <Input
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    placeholder="Bank transfer reference or receipt number"
                  />
                </div>
                <div>
                  <Label>Notes *</Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter payment notes (min 5 chars)"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleMarkPaid} disabled={loading} className="bg-green-600 hover:bg-green-700">
                    {loading ? 'Processing...' : 'Confirm Payment'}
                  </Button>
                  <Button variant="outline" onClick={() => { setAction(null); setReason(''); setPaymentRef(''); }}>Cancel</Button>
                </div>
              </div>
            </Card>
          )}

          {action === 'reject' && (
            <Card className="p-4 border-red-200 bg-red-50">
              <h4 className="font-medium mb-3">Reject Subscription</h4>
              <div className="space-y-3">
                <div>
                  <Label>Rejection Reason *</Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for rejection (min 5 chars)"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleReject} disabled={loading} variant="destructive">
                    {loading ? 'Processing...' : 'Confirm Rejection'}
                  </Button>
                  <Button variant="outline" onClick={() => { setAction(null); setReason(''); }}>Cancel</Button>
                </div>
              </div>
            </Card>
          )}

          {action === 'cancel' && (
            <Card className="p-4 border-gray-200 bg-gray-50">
              <h4 className="font-medium mb-3">Cancel Subscription</h4>
              <div className="space-y-3">
                <div>
                  <Label>Cancellation Reason *</Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for cancellation (min 5 chars)"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCancel} disabled={loading} variant="outline">
                    {loading ? 'Processing...' : 'Confirm Cancellation'}
                  </Button>
                  <Button variant="outline" onClick={() => { setAction(null); setReason(''); }}>Back</Button>
                </div>
              </div>
            </Card>
          )}
        </div>

        <DialogFooter className="flex justify-between gap-2 flex-wrap">
          {/* Workflow Actions */}
          {subscription.status === 'pending' && !action && (
            <>
              <Button onClick={() => setAction('approve')} className="bg-blue-600 hover:bg-blue-700" data-testid="approve-subscription-btn">
                <Check className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button onClick={() => setAction('reject')} variant="destructive" data-testid="reject-subscription-btn">
                <X className="h-4 w-4 mr-1" /> Reject
              </Button>
            </>
          )}
          {subscription.status === 'approved' && !action && (
            <>
              <Button onClick={() => setAction('payment')} className="bg-green-600 hover:bg-green-700" data-testid="mark-paid-btn">
                <CreditCard className="h-4 w-4 mr-1" /> Mark Payment Received
              </Button>
              <Button onClick={() => setAction('cancel')} variant="outline" data-testid="cancel-subscription-btn">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
            </>
          )}
          {subscription.status === 'paid' && !action && (
            <Button onClick={handleAllocate} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="allocate-units-btn">
              <Coins className="h-4 w-4 mr-1" /> Allocate Units
            </Button>
          )}
          {!action && (
            <Button variant="outline" onClick={() => onEdit(subscription)} data-testid="edit-subscription-btn">
              <PencilSimple className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Create Subscription Dialog
 */
const CreateSubscriptionDialog = ({ open, onOpenChange, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [parties, setParties] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [formData, setFormData] = useState({
    party_id: '',
    security_id: '',
    units_requested: '',
    notes: ''
  });
  const [selectedInstrument, setSelectedInstrument] = useState(null);

  useEffect(() => {
    if (open) {
      fetchParties();
      fetchInstruments();
    }
  }, [open]);

  const fetchParties = async () => {
    try {
      const res = await api.get('/admin/v2/parties?party_type=investor_individual&limit=500');
      setParties(res.data.parties || []);
    } catch (err) {
      console.error('Failed to load parties', err);
    }
  };

  const fetchInstruments = async () => {
    try {
      const res = await api.get('/admin/projects/instruments/all');
      setInstruments((res.data.instruments || []).filter(i => i.status !== 'retired'));
    } catch (err) {
      console.error('Failed to load instruments', err);
    }
  };

  const handleInstrumentChange = (securityId) => {
    setFormData({ ...formData, security_id: securityId });
    setSelectedInstrument(instruments.find(i => i.id === securityId));
  };

  const handleSubmit = async () => {
    if (!formData.party_id || !formData.security_id || !formData.units_requested) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/admin/v2/subscriptions', {
        ...formData,
        units_requested: parseInt(formData.units_requested)
      });
      toast.success('Subscription created');
      onCreated();
      onOpenChange(false);
      setFormData({ party_id: '', security_id: '', units_requested: '', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = selectedInstrument && formData.units_requested
    ? parseFloat(selectedInstrument.unit_price || 0) * parseInt(formData.units_requested || 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Subscription</DialogTitle>
          <DialogDescription>
            Create a new subscription on behalf of an investor
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Investor *</Label>
            <Select value={formData.party_id} onValueChange={(v) => setFormData({ ...formData, party_id: v })}>
              <SelectTrigger data-testid="select-investor">
                <SelectValue placeholder="Select investor" />
              </SelectTrigger>
              <SelectContent>
                {parties.map(party => (
                  <SelectItem key={party.id} value={party.id}>
                    {party.display_name} ({party.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Instrument *</Label>
            <Select value={formData.security_id} onValueChange={handleInstrumentChange}>
              <SelectTrigger data-testid="select-instrument">
                <SelectValue placeholder="Select instrument" />
              </SelectTrigger>
              <SelectContent>
                {instruments.map(instrument => (
                  <SelectItem key={instrument.id} value={instrument.id}>
                    {instrument.name} - €{parseFloat(instrument.unit_price || 0).toLocaleString()}/unit
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedInstrument && (
            <Card className="p-3 bg-blue-50">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-foreground-muted">Unit Price</Label>
                  <p className="font-medium">€{parseFloat(selectedInstrument.unit_price || 0).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Min Units</Label>
                  <p className="font-medium">{selectedInstrument.min_subscription_units || 1}</p>
                </div>
                <div>
                  <Label className="text-xs text-foreground-muted">Available</Label>
                  <p className="font-medium">{selectedInstrument.units_available || 0}</p>
                </div>
              </div>
            </Card>
          )}

          <div>
            <Label>Units Requested *</Label>
            <Input
              type="number"
              value={formData.units_requested}
              onChange={(e) => setFormData({ ...formData, units_requested: e.target.value })}
              placeholder="Enter number of units"
              min={selectedInstrument?.min_subscription_units || 1}
              data-testid="units-input"
            />
          </div>

          {totalAmount > 0 && (
            <div className="p-3 bg-gray-100 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-foreground-muted">Total Amount</span>
                <span className="text-xl font-bold text-primary">€{totalAmount.toLocaleString()}</span>
              </div>
            </div>
          )}

          <div>
            <Label>Notes</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="create-subscription-submit">
            {loading ? 'Creating...' : 'Create Subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Subscription Management Page - Mobile optimized
 */
export default function AdminSubscriptionsPage() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [filterInstrument, setFilterInstrument] = useState('all');
  const [sort, setSort] = useState('newest');
  const [stats, setStats] = useState({ total: 0, pending: 0 });

  useEffect(() => {
    fetchSubscriptions();
    fetchStats();
  }, [filter]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await api.get(`/admin/v2/subscriptions${params}`);
      setSubscriptions(res.data.subscriptions || []);
    } catch (err) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/v2/subscriptions/stats/summary');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  const openDetail = async (sub) => {
    try {
      const res = await api.get(`/admin/v2/subscriptions/${sub.id}`);
      setSelectedSubscription(res.data);
      setDetailOpen(true);
    } catch (err) {
      toast.error('Failed to load subscription details');
    }
  };

  const instrumentOptions = [...new Map(
    subscriptions.filter(s => s.offering_name).map(s => [s.offering_name, s.offering_name])
  )].map(([value, label]) => ({ value, label }));

  const filteredSubscriptions = (() => {
    const q = search.trim().toLowerCase();
    let list = subscriptions.filter((sub) => {
      if (q && ![sub.code, sub.party_name, sub.offering_name].filter(Boolean).join(' ').toLowerCase().includes(q)) return false;
      if (filterInstrument !== 'all' && sub.offering_name !== filterInstrument) return false;
      return true;
    });
    if (sort === 'amount') list = [...list].sort((a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0));
    return list;
  })();

  return (
    <div className="max-w-[1200px] space-y-6" data-testid="admin-subscriptions-page">
      <AdminPageHeader
        eyebrow="Capital"
        title="Subscriptions"
        subtitle="Review, approve and allocate investor subscriptions."
        actions={(
          <Button onClick={() => setCreateOpen(true)} size="sm" data-testid="create-subscription-btn">
            <Plus className="h-4 w-4 md:mr-1.5" />
            <span className="hidden md:inline">New subscription</span>
          </Button>
        )}
      />

      <AdminKpiStrip>
        <AdminKpi label="Total" value={stats.total} />
        <AdminKpi label="Pending" value={stats.pending} tone="text-amber-600" />
        <AdminKpi label="Approved" value={stats.by_status?.approved?.count || 0} />
        <AdminKpi label="Allocated" value={stats.by_status?.allocated?.count || 0} />
      </AdminKpiStrip>


      <AdminToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search by code, investor or instrument…"
        filters={[
          { key: 'status', label: 'Status', options: [
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'paid', label: 'Paid' },
            { value: 'allocated', label: 'Allocated' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'cancelled', label: 'Cancelled' },
          ] },
          { key: 'instrument', label: 'Instrument', options: instrumentOptions },
        ]}
        values={{ status: filter, instrument: filterInstrument }}
        onFilter={(key, value) => (key === 'status' ? setFilter(value) : setFilterInstrument(value))}
        onClear={() => { setFilter('all'); setFilterInstrument('all'); }}
        sortOptions={[
          { value: 'newest', label: 'Newest first' },
          { value: 'amount', label: 'Largest amount' },
        ]}
        sort={sort}
        onSort={setSort}
        resultLabel={`${filteredSubscriptions.length} of ${subscriptions.length} subscriptions`}
      />

      {/* Subscriptions List - Compact cards on mobile */}
        {loading ? (
          <div className="flex items-center justify-center py-10 sm:py-12">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center">
            <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-foreground-muted mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium mb-2">No Subscriptions</h3>
            <p className="text-sm text-foreground-muted mb-4">
              {filter !== 'all' ? `No "${filter}" subscriptions` : 'No subscriptions yet'}
            </p>
            <Button onClick={() => setCreateOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" /> Create First
            </Button>
          </Card>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {filteredSubscriptions.map(sub => {
              const status = statusConfig[sub.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              return (
                <Card
                  key={sub.id}
                  className="p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openDetail(sub)}
                  data-testid={`subscription-card-${sub.code}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                      <div className={cn('p-1.5 sm:p-2 rounded-lg flex-shrink-0', status.color.split(' ')[0])}>
                        <StatusIcon className="h-4 w-4 sm:h-5 sm:w-5" weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                          <span className="font-mono text-xs sm:text-sm font-medium">{sub.code}</span>
                          <Badge className={cn('text-[10px] sm:text-xs px-1.5', status.color)}>{status.label}</Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-foreground-muted truncate">
                          {sub.party_name || 'Unknown'} • {sub.offering_name || 'Unknown'}
                        </p>
                        {/* Show amount on mobile inline */}
                        <p className="text-xs font-medium text-primary sm:hidden mt-0.5">
                          €{parseFloat(sub.total_amount || 0).toLocaleString()} • {sub.units_requested} units
                        </p>
                      </div>
                    </div>
                    <div className="text-right hidden sm:block flex-shrink-0">
                      <p className="font-bold">€{parseFloat(sub.total_amount || 0).toLocaleString()}</p>
                      <p className="text-sm text-foreground-muted">{sub.units_requested?.toLocaleString()} units</p>
                    </div>
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-foreground-muted flex-shrink-0" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Dialogs */}
        <SubscriptionDetailDialog
          subscription={selectedSubscription}
          open={detailOpen}
          onOpenChange={setDetailOpen}
          onRefresh={() => {
            fetchSubscriptions();
            fetchStats();
            if (selectedSubscription) {
              openDetail(selectedSubscription);
            }
          }}
          onEdit={(sub) => {
            setDetailOpen(false);
            setEditing(sub);
          }}
        />
        <SubscriptionEditDialog
          subscription={editing}
          open={!!editing}
          onOpenChange={(next) => !next && setEditing(null)}
          onSaved={() => {
            fetchSubscriptions();
            fetchStats();
          }}
        />
        <CreateSubscriptionDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => {
            fetchSubscriptions();
            fetchStats();
          }}
        />
    </div>
  );
}
